(ns app.front.process
  (:require [app.domain.application.guards :as shared-guards]
            [app.domain.application.shared-process :as sp]
            [app.front.entities :as entities]
            [app.front.process.save-tracking :as save-tracking]
            [app.front.view.workspace.subs :as ws-subs]
            [app.process.model :as process-model]
            [core.front.event :as event]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [tilakone.core :as tk]))

(rf/reg-event-fx ::fetch-process-states
  (fn [_ [_ country permit-type process-flow]]
    {:dispatch [:comm/command-monitored
                {:command    :group/process-states
                 :method     :get
                 :data       (-> (util/mmap country permit-type)
                                 (p/assoc-if :process-flow process-flow))
                 :on-success [::fetch-process-states.success
                              country
                              permit-type
                              process-flow]}]}))

(rf/reg-event-db ::fetch-process-states.success
  (fn [db [_ country permit-type process-flow _ fsm]]
    (assoc-in db [::process-states country permit-type process-flow] fsm)))

(rf/reg-event-fx ::fetch-process-states-for-one
  (fn [_ [_ fsm-target country permit-type process-flow]]
    {:comm/command {:command    :group/process-states-for-entity
                    :method     :get
                    :data       (-> (util/mmap country permit-type fsm-target)
                                    (p/assoc-if :process-flow process-flow))
                    :on-success [::fetch-process-states-one.success
                                 fsm-target
                                 country
                                 permit-type
                                 process-flow]}}))

(rf/reg-event-db ::fetch-process-states-one.success
  (fn [db [_ fsm-target country permit-type process-flow _ fsm]]
    (assoc-in db [::process-states country permit-type process-flow fsm-target] fsm)))

(rf/reg-sub ::process-states
  :-> ::process-states)

(rf/reg-sub ::current-ws-process-states
  :<- [::process-states]
  :<- [::ws-subs/country]
  :<- [::ws-subs/permit-type]
  :<- [::ws-subs/process-flow]
  (fn [[process-states country permit-type process-flow] _]
    (get-in process-states [country permit-type process-flow])))

(rf/reg-sub ::generic-process-states
  :<- [::process-states]
  (fn [process-states [_ country permit-type process-flow]]
    (get-in process-states [country permit-type process-flow])))

;;
;; Guards:
;;

(defn get-granted-permissions [fsm]
  (-> fsm :request :target :granted-permissions set))

(def required-permissions
  {:name    :process.guard/required-permissions
   :type    :guard
   :handler (fn [fsm]
              (let [granted-permissions  (get-granted-permissions fsm)
                    required-permissions (-> fsm ::tk/guard second)]
                (every? granted-permissions required-permissions)))})


(def required-permissions-one-of
  {:name    :process.guard/required-permissions-one-of
   :type    :guard
   :handler (fn [fsm]
              (let [granted-permissions  (get-granted-permissions fsm)
                    required-permissions (-> fsm ::tk/guard second)]
                (some (set required-permissions) granted-permissions)))})

(def is-super-admin?
  {:name    :process.guard/is-super-admin?
   :type    :guard
   :handler (fn [{:keys [:core.user/user]}]
              (-> user
                  :user/admin-permissions
                  (contains? :permission/admin)))})

(def guards (p/index-by :name (concat [required-permissions
                                       required-permissions-one-of
                                       is-super-admin?]
                                      shared-guards/guards)))


(defn- missing-guard! [fsm]
  (throw (ex-info (str "missing guard: " (-> fsm ::tk/guard)) {:guard (-> fsm ::tk/guard)})))


(defn guard? [fsm]
  (let [guard   (-> fsm ::tk/guard first guards (or (missing-guard! fsm)))
        handler (-> guard :handler)]
    (handler fsm)))


;;
;; Process:
;;


(defn- match?
  "Matcher to tilakone, match signal if the first element of signal (see Signal above)
  match the `:tilakone.core/on` in tilakone states."
  [signal on]
  (= (nth signal 0) on))


(defn- entity-process
  "Returns the state-machine for the given entity."
  [enabled-flags db target-entity view-data process-states user map-config]
  {:request        {:target        target-entity
                    :db            db
                    :feature-flags enabled-flags
                    :map-config    map-config}
   :core.user/user user
   :view-data      view-data
   ::tk/states     (get process-states (:entity/type target-entity))
   ::tk/state      (-> target-entity :process/state)
   ::tk/match?     match?
   ::tk/guard?     guard?
   ::target        target-entity})


(defn signal-permitted? [process signal]
  (when (and process signal)
    (nil? (process-model/rejected-by-guards process signal))))


(rf/reg-event-fx ::signal
  ;; `opts` may carry, besides the callbacks and `:notify`:
  ;;   :track-save?  ask the save indicator to report this signal (see `save-tracking`)
  ;;   :operation-id reuse an existing registry entry instead of opening a new one
  ;;   :chained?     a follow-up request will finish this operation, so a 200 does not settle it
  (fn [{:keys [db]} [_ entity-or-eid signal & [{:keys [operation-id chained? track-save?] :as opts}]]]
    (let [entity-id    (get entity-or-eid :db/id entity-or-eid)
          signal-id    (first signal)
          operation-id (or operation-id (random-uuid))]
      {:comm/command {:command         :process/apply-signal
                      :data            {:entity-id      entity-id
                                        :process/signal signal}
                      :on-success      [::signal-success operation-id opts]
                      :on-bad-request  [::signal-failure operation-id opts]
                      :on-done         [::signal-done-fallback operation-id opts]
                      :on-error        [::signal-error-fallback operation-id opts :process/apply-signal]
                      :on-server-error [::signal-error-fallback operation-id opts :process/apply-signal]}
       ;; Whether this is tracked is decided here rather than on completion, so a response that
       ;; lands after the user navigated away behaves the same as one that lands in place.
       :db           (save-tracking/start-operation
                       db
                       operation-id
                       {:signal-id    signal-id
                        :workspace-id (save-tracking/current-workspace-id db)
                        :track-save?  (and track-save? (save-tracking/tracking-enabled? db))
                        :chained?     chained?})})))


(defn on-done-dispatch [fx {:keys [on-done]} status body]
  (event/add-to-dispatch fx (when on-done (into on-done [status body]))))


(defn on-success-dispatch [fx {:keys [on-success]} status body]
  (event/add-to-dispatch fx (when on-success (into on-success [status body]))))


(defn on-error-dispatch [fx {:keys [on-error]} status body]
  (event/add-to-dispatch fx (when on-error (into on-error [status body]))))


(defn notify [fx result-key {:keys [notify]}]
  (event/add-to-dispatch fx (p/if-all-let [msg-key (get notify result-key)
                                           kind    (if (= result-key :success)
                                                     :success
                                                     :error)]
                              [:notifications/add :toast kind (if (= result-key :success)
                                                                :success
                                                                :error/error) msg-key])))


(rf/reg-event-fx ::signal-success
  (fn [{:keys [db]} [_ operation-id opts status body]]
    (-> {:dispatch-n [[::entities/apply-changes status body]]
         :db         (save-tracking/succeed-operation db operation-id)}
        (on-success-dispatch opts status body)
        (on-done-dispatch opts status body)
        (notify :success opts))))


(rf/reg-event-fx ::signal-failure
  (fn [{:keys [db]} [_ operation-id opts status body request-id]]
    (-> {:dispatch [:notifications/add
                    :toast
                    :error
                    (or (:error-title body) :error/error)
                    (or (:error body) :error-with-req-id/rejected-fsm-signal)
                    request-id]
         :db       (save-tracking/fail-operation db operation-id)}
        (on-done-dispatch opts status body))))


;; below fallbacks ensure that operations left in flight by an unhandled response are settled

(rf/reg-event-fx ::signal-done-fallback
  (fn [{:keys [db]} [_ operation-id opts status body request-id]]
    (-> {:comm/response {:response [status body request-id]}
         :db            (if (= 200 status)
                          (save-tracking/succeed-operation db operation-id)
                          (save-tracking/fail-operation db operation-id))}
        (on-done-dispatch opts status body))))


(rf/reg-event-fx ::signal-error-fallback
  (fn [{:keys [db]} [_ operation-id opts command http-status error-body request-id]]
    (-> {:comm/error {:command    command
                      :status     http-status
                      :body       error-body
                      :request-id request-id}
         :db         (save-tracking/fail-operation db operation-id)}
        (notify :error opts)
        (on-error-dispatch opts http-status error-body))))


(rf/reg-sub ::current-processing-signals
  (fn [db _]
    (save-tracking/processing-signal-ids db (save-tracking/current-workspace-id db))))


(rf/reg-sub ::signal-processing?
  :<- [::current-processing-signals]
  (fn [signals [_ & signals-or-ids]]
    (->> signals-or-ids
         (some #(contains? signals
                           (if (vector? %) (first %) %)))
         boolean)))

(defn- make-entity-process-signal [entity-sub]
  (fn [_]
    [(rf/subscribe [::ff/enabled-flags])
     (rf/subscribe [::entities/context])
     (rf/subscribe [::ws-subs/view-data])
     (rf/subscribe [::current-ws-process-states])
     (rf/subscribe [:session/user])
     (rf/subscribe [::ws-subs/municipality-config])
     (rf/subscribe entity-sub)]))

(defn- entity-process-handler [[enabled-flags ctx view-data process-states user map-config process-target] _]
  (entity-process enabled-flags ctx process-target view-data process-states user map-config))

(rf/reg-sub ::application-process
  (make-entity-process-signal [::ws-subs/application])
  entity-process-handler)

(rf/reg-sub ::execution-process
  (make-entity-process-signal [::ws-subs/execution])
  entity-process-handler)

(rf/reg-sub ::periodic-parent-process
  (make-entity-process-signal [::ws-subs/periodic-parent])
  entity-process-handler)

(rf/reg-sub ::pre-consultation-process
  (make-entity-process-signal [::ws-subs/pre-consultation])
  entity-process-handler)

(rf/reg-sub ::archiving-process
  (make-entity-process-signal [::ws-subs/archiving])
  entity-process-handler)

(rf/reg-sub ::entity-process
  :<- [::ff/enabled-flags]
  :<- [::entities/context]
  :<- [::ws-subs/view-data]
  :<- [::current-ws-process-states]
  :<- [:session/user]
  :<- [::ws-subs/municipality-config]
  (fn [[enabled-flags ctx view-data process-states user map-config] [_ process-target]]
    (let [process-target (if (entities/entity-id? process-target)
                           (entities/find-entity ctx process-target)
                           process-target)]
      (entity-process enabled-flags ctx process-target view-data process-states user map-config))))

(rf/reg-sub ::generic-entity-process
  ;; process for entities other than phase-entities
  (fn [[_ country permit-type process-flow]]
    [(rf/subscribe [::ff/enabled-flags])
     (rf/subscribe [::entities/context])
     (rf/subscribe [::generic-process-states country permit-type process-flow])
     (rf/subscribe [:session/user])])
  (fn [[enabled-flags ctx process-states user]
       [_ _ _ _ process-target]]
    (let [process-target (entities/find-entity ctx process-target)]
      (entity-process enabled-flags ctx process-target nil process-states user nil))))

(defn- make-entity-process-sub-handler [process-fn]
  (fn [[enabled-flags ctx view-data process-states user map-config] [_ target-entity-or-id arg]]
    (let [target-entity (if (entities/entity-id? target-entity-or-id)
                          (entities/find-entity ctx target-entity-or-id)
                          target-entity-or-id)]
      (process-fn
        (entity-process enabled-flags ctx target-entity view-data process-states user map-config)
        arg))))

(rf/reg-sub ::signal-permitted?
  :<- [::ff/enabled-flags]
  :<- [::entities/context]
  :<- [::ws-subs/view-data]
  :<- [::current-ws-process-states]
  :<- [:session/user]
  :<- [::ws-subs/municipality-config]
  (make-entity-process-sub-handler signal-permitted?))

(rf/reg-sub ::signals-available
  :<- [::ff/enabled-flags]
  :<- [::entities/context]
  :<- [::ws-subs/view-data]
  :<- [::current-ws-process-states]
  :<- [:session/user]
  :<- [::ws-subs/municipality-config]
  (make-entity-process-sub-handler
    (fn [process signals] (filter #(process-model/signal-available? process %) signals))))

(defn signal-permitted-handler [process [_ signal]]
  (signal-permitted? process signal))

(rf/reg-sub ::app-signal-permitted?
  :<- [::application-process]
  signal-permitted-handler)

(rf/reg-sub ::execution-signal-permitted?
  :<- [::execution-process]
  signal-permitted-handler)

(rf/reg-sub ::periodic-parent-signal-permitted?
  :<- [::periodic-parent-process]
  signal-permitted-handler)

(rf/reg-sub ::archiving-signal-permitted?
  :<- [::archiving-process]
  signal-permitted-handler)

(rf/reg-sub ::pre-consultation-signal-permitted?
  :<- [::pre-consultation-process]
  signal-permitted-handler)

;; Checks if signal is forbidden, i.e. cannot become possible. This is
;; the check to do when deciding whether to hide a button completely, button
;; even not-forbidden signals may not pass all checks. Use *-permitted? for
;; that.
(rf/reg-sub ::signal-forbidden?
  :<- [::ff/enabled-flags]
  :<- [::entities/context]
  :<- [::ws-subs/view-data]
  :<- [::current-ws-process-states]
  :<- [:session/user]
  :<- [::ws-subs/municipality-config]
  (fn [[enabled-flags ctx view-data process-states user map-config] [_ target-entity-or-id signal]]
    (let [target-entity (if (entities/entity-id? target-entity-or-id)
                          (entities/find-entity ctx target-entity-or-id)
                          target-entity-or-id)
          process       (entity-process enabled-flags ctx target-entity view-data process-states user map-config)
          results       (tk/apply-guards process signal)]
      (if (empty? results)
        true ;; signal does not exist in this state
        (->> results
             (every? (fn [[_ guard-results]]
                       (sp/route-forbidden? guard-results))))))))

(rf/reg-sub ::signal-errors
  (fn [[_ process-target]]
    (rf/subscribe [::entity-process process-target]))
  (fn [process [_ _ signal]]
    (when signal
      (process-model/rejected-by-guards process signal))))

(rf/reg-sub ::signal-errors-for-error-list
  (fn [[_ process-target _]]
    (rf/subscribe [::entity-process process-target]))
  (fn [process [_ _ signal]]
    (when signal
      (let [results (->> (tk/apply-guards process signal)
                         (map second))]
        (if (empty? results)
          [[:process/illegal-signal nil]]
          (sp/visible-errors-for-results results))))))


(def workspace-process-subs {:workspace.state/application      ::application-process
                             :workspace.state/execution        ::execution-process
                             :workspace.state/pre-consultation ::pre-consultation-process
                             :workspace.state/periodic-parent  ::periodic-parent-process
                             :workspace.state/archiving        ::archiving-process
                             :workspace.state/archived         ::archiving-process})


(def workspace-process-target-subs {:workspace.state/application      ::ws-subs/application
                                    :workspace.state/execution        ::ws-subs/execution
                                    :workspace.state/pre-consultation ::ws-subs/pre-consultation
                                    :workspace.state/periodic-parent  ::ws-subs/periodic-parent
                                    :workspace.state/archiving        ::ws-subs/archiving
                                    :workspace.state/archived         ::ws-subs/archiving})
